import {
  getAuth,
  multiFactor,
  reauthenticateWithCredential,
  EmailAuthProvider,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
  type MultiFactorResolver,
  type UserCredential,
  type MultiFactorError,
} from "firebase/auth";
import { getCurrentUser } from "../auth";

export const signInResolver = (
  error: MultiFactorError
): MultiFactorResolver => {
  return getMultiFactorResolver(getAuth(), error);
};

export const reAuthenticate = async (password: string): Promise<void> => {
  const user = await getCurrentUser();
  const credential = EmailAuthProvider.credential(user!.email!, password);
  await reauthenticateWithCredential(user!, credential);
};

export const is2FAEnabled = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  return !!user && multiFactor(user).enrolledFactors.length > 0;
};

export const verify = async (
  resolver: MultiFactorResolver,
  verificationId: string,
  code: string
): Promise<UserCredential> => {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  const userCredential = await resolver.resolveSignIn(assertion);
  return userCredential;
};

export const verifyAndRemove2FA = async (
  resolver: MultiFactorResolver,
  verificationId: string,
  code: string
): Promise<void> => {
  const userCredential = await verify(resolver, verificationId, code);
  const mfa = multiFactor(userCredential.user);
  await Promise.all(
    mfa.enrolledFactors
      .filter(
        (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      )
      .map((factor) => mfa.unenroll(factor))
  );
};

export const verifyAndAdd2FA = async (
  verificationId: string,
  code: string
): Promise<void> => {
  const user = await getCurrentUser();
  const phoneCredential = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(phoneCredential);
  await multiFactor(user!).enroll(assertion, "main phone number");
};

export const sendEnrollmentCode = async (
  phoneNumber: string
): Promise<string> => {
  const auth = getAuth();
  const user = await getCurrentUser();
  const session = await multiFactor(user!).getSession();
  const recaptcha = new RecaptchaVerifier(auth, "recaptcha-container", {
    size: "invisible",
  });
  const provider = new PhoneAuthProvider(auth);
  const verificationId = await provider.verifyPhoneNumber(
    { phoneNumber, session },
    recaptcha
  );
  return verificationId;
};

export const resolveMFAChallenge = async (
  error: MultiFactorError
): Promise<{
  resolver: MultiFactorResolver;
  verificationId: string;
}> => {
  const auth = getAuth();
  const resolver = getMultiFactorResolver(auth, error);
  const phoneInfoOptions = {
    multiFactorHint: resolver?.hints[0],
    session: resolver.session,
  };
  const recaptcha = new RecaptchaVerifier(auth, "recaptcha-container", {
    size: "invisible",
  });
  const provider = new PhoneAuthProvider(auth);
  const newVerificationId = await provider.verifyPhoneNumber(
    phoneInfoOptions,
    recaptcha
  );
  return { resolver, verificationId: newVerificationId };
};
